import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  FormLabel,
  Grid,
  GridItem,
  Input,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import {
  DEFAULT_PROMO_CODE_SETTINGS,
  OccupancyType,
  PageType,
  Special,
  SpecialGroup,
  StudentSelectionMode,
  SystemTranslationKeys,
} from '@common/entities';
import styled from '@emotion/styled';
import { updateSelectedSpecials, updateSpecials } from 'state/slices/unitSelectionSlice';
import { RootState } from 'state/store';

import { CrossIcon, PencilIcon } from 'components/custom-icons';
import { ConfirmationModal, OkModal, SpecialsModal } from 'components/modal';
import { useSystemTranslation } from 'hooks';
import { useSaveOptionsPageSynchronously } from 'hooks/addons/useSaveOptionsPageSynchronously';
import { useStudentSelectionMode } from 'hooks/floor-plans';
import { useGetPageData } from 'hooks/useGetPageData';
import { useSaveResponseSynchronously } from 'hooks/useSaveResponseSynchronously';
import { getSpecials, validateSpecials } from 'services/specials';
import handleError from 'utils/handleError';
import { getPageIndex } from 'utils/page-utils';
import {
  getAllPromoCodeSpecialsForUnit,
  getAllStandardSpecialsForUnit,
  getSinglePromoGiftToAutoApply,
} from 'utils/specials';
import useRefreshScheduledCharges from 'utils/useRefreshScheduledCharges';

export function SpecialsCard() {
  const { t, tSystem } = useSystemTranslation();
  const {
    floorPlan,
    unit,
    propertySpecials,
    floorplanSpecials,
    unitSpaceSpecials,
    unitTypeSpecials,
    addOns,
    selectedSpecials,
    promoCodeSpecials,
  } = useSelector((state: RootState) => state.unitSelection);
  const dispatch = useDispatch();
  const { isOpen: isShowTCOpen, onOpen: onShowTCOpen, onClose: onShowTCClose } = useDisclosure();
  const {
    isOpen: isSpecialsModalOpen,
    onOpen: onSpecialsModalOpen,
    onClose: onSpecialsModalClose,
  } = useDisclosure();
  const {
    isOpen: isRemoveSpecialGroupOpen,
    onOpen: onRemoveSpecialGroupOpen,
    onClose: onRemoveSpecialGroupClose,
  } = useDisclosure();
  const [termsAndConditions, setTermsAndConditions] = useState<string>('');
  const [specialGroupToEdit, setSpecialGroupToEdit] = useState<any>(null);
  const [selectedSpecialsInGroupToEdit, setselectedSpecialsInGroupToEdit] = useState<any>(null);
  const [specialGroupRemoveConfirmationMessage, setspecialGroupRemoveConfirmationMessage] =
    useState<string>('');
  const [promoCode, setPromoCode] = useState<string>('');
  const [promoCodeStatusColor, setPromoCodeStatusColor] = useState<string>('');
  const [promoCodeErrorVisibility, setPromoCodeErrorVisibility] = useState<string>('hidden');
  const [promoCodeErrorMessage, setPromoCodeErrorMessage] = useState<string>('');
  const [isSavingSpecials, setIsSavingSpecials] = useState<boolean>(false);
  const [isValidatingPromoCode, setIsValidatingPromoCode] = useState<boolean>(false);
  const [isDeletingSpecial, setIsDeletingSpecial] = useState<boolean>(false);
  const allStandardSpecialsForUnit = getAllStandardSpecialsForUnit(
    floorPlan,
    unit,
    propertySpecials,
    floorplanSpecials,
    unitSpaceSpecials,
    unitTypeSpecials,
  );
  const allPromoCodeSpecialsForUnit = getAllPromoCodeSpecialsForUnit(
    floorPlan,
    unit,
    promoCodeSpecials,
  );
  const allAvailableSpecialsForUnit = allPromoCodeSpecialsForUnit.concat(
    allStandardSpecialsForUnit,
  );
  const visibleSelectedSpecials = selectedSpecials.filter((selectedSpecial) =>
    allAvailableSpecialsForUnit.some(
      (specialGroup) =>
        specialGroup.groupId === selectedSpecial.groupId &&
        specialGroup.specials?.some(
          (special: Special) => String(special.specialId) === String(selectedSpecial.specialId),
        ),
    ),
  );
  const { propertyId, pages, applicationId, leaseId } = useSelector((state: RootState) => {
    const { form, applicant } = state.application;
    return {
      pages: form.pages,
      propertyId: applicant.propertyId,
      applicationId: applicant.currentResponse.applicationId,
      leaseId: applicant.currentResponse.leaseId,
    };
  });
  const { occupancyType } = useSelector((state: RootState) => state.application.form);
  const { isApplicationLocked } = useSelector((state: RootState) => state.fieldSettings);
  const { leasingJourneyErrors } = useSelector((state: RootState) => state.navigation);
  const studentSelectedSpace = useSelector(
    (state: RootState) => state.studentUnitSelection.selectedSpace,
  );
  const storedPromoCodeSettings = useSelector(
    (state: RootState) => state.applicationSettings.promoCode,
  );
  const studentSelectionMode = useStudentSelectionMode();
  const addOnPageIndex = getPageIndex(pages, PageType.Options);
  const isPromoEntryEnabled = storedPromoCodeSettings?.enabled ?? false;
  const promoCodeLabel =
    storedPromoCodeSettings?.descriptiveText?.trim() || DEFAULT_PROMO_CODE_SETTINGS.descriptiveText;

  const { patchSynchronously } = useSaveResponseSynchronously();
  const getPageData = useGetPageData();
  const { saveOptionsPageSynchronously } = useSaveOptionsPageSynchronously({
    activeStepIndex: addOnPageIndex === -1 ? 0 : addOnPageIndex,
  });
  const { refreshScheduledCharges } = useRefreshScheduledCharges({
    propertyId,
    leaseId,
  });

  function showTermsAndConditions(terms: string) {
    setTermsAndConditions(terms);
    onShowTCOpen();
  }

  function showSpecialsModal(specialGroup: SpecialGroup, selectedSpecialsInGroup: Special[]) {
    setSpecialGroupToEdit(specialGroup);
    setselectedSpecialsInGroupToEdit(selectedSpecialsInGroup);
    onSpecialsModalOpen();
  }

  function openConfirmationModalForRemovingSpecials(specialGroup: SpecialGroup) {
    setSpecialGroupToEdit(specialGroup);
    if (specialGroup.couponCode) {
      setspecialGroupRemoveConfirmationMessage(`Are you sure you want to remove the special promotion 
        ${specialGroup.couponCode} - ${specialGroup.groupName}?`);
    } else {
      setspecialGroupRemoveConfirmationMessage(`Are you sure you want to remove the special promotion 
        ${specialGroup.groupName}? You can reselect this special on the Add-Ons page.`);
    }
    onRemoveSpecialGroupOpen();
  }

  async function handleSpecialGroupRemove() {
    setIsDeletingSpecial(true);
    try {
      const newSelectedSpecials = selectedSpecials.filter(
        (special: Special) => special.groupId !== specialGroupToEdit.groupId,
      );
      await updateSpecialsInApplicantResponse(newSelectedSpecials);
      dispatch(updateSelectedSpecials(newSelectedSpecials));

      onRemoveSpecialGroupClose();
    } catch (error) {
      if (addOnPageIndex === -1) {
        handleError(`Form Viewer - Error saving specials: ${error}`, 'Error saving specials');
      }
    } finally {
      setIsDeletingSpecial(false);
    }
  }

  const showPromoCodeError = (message?: string) => {
    setPromoCodeErrorVisibility('visible');
    setPromoCodeStatusColor('red');
    setPromoCodeErrorMessage(
      message?.trim() ||
        t(
          SystemTranslationKeys.cart.invalidPromoCode,
          'The promo code you entered is invalid. Please try again.',
        ),
    );
  };

  const applyPromoCode = async () => {
    const trimmedCode = promoCode.trim();
    if (!trimmedCode) {
      showPromoCodeError();
      return;
    }

    setIsValidatingPromoCode(true);
    try {
      const spaceConfigurationIdRaw =
        unit?.selectedSpace?.spaceConfigurationId ?? studentSelectedSpace?.spaceConfigurationId;
      const spaceConfigurationId =
        spaceConfigurationIdRaw != null && Number(spaceConfigurationIdRaw) > 0
          ? Number(spaceConfigurationIdRaw)
          : undefined;

      const validateSpecialsResponse = await validateSpecials(
        false,
        trimmedCode,
        spaceConfigurationId,
      );
      const validationStatus = validateSpecialsResponse?.data?.result?.status;
      // Fail closed (same as BottomNavigation): missing/malformed status must not apply.
      if (validationStatus !== 'success') {
        showPromoCodeError(validateSpecialsResponse?.data?.result?.message);
        return;
      }

      let promoCodeSpecial: SpecialGroup | undefined;
      try {
        const specialDetails = await getSpecials();
        const serverPromoCodeSpecials = getAllPromoCodeSpecialsForUnit(
          floorPlan,
          unit,
          Array.isArray(specialDetails)
            ? specialDetails.filter((special: SpecialGroup) => special.couponCode != null)
            : [],
        );
        promoCodeSpecial = serverPromoCodeSpecials.find(
          (special: SpecialGroup) =>
            normalizePromoCode(special.couponCode) === normalizePromoCode(trimmedCode),
        );
      } catch {
        // Preserve the existing loaded-specials flow when refreshing details fails.
      }

      promoCodeSpecial ??= allPromoCodeSpecialsForUnit.find(
        (special) => normalizePromoCode(special.couponCode) === normalizePromoCode(trimmedCode),
      );
      if (!promoCodeSpecial) {
        showPromoCodeError(
          'The promo code was validated, but its special details are not available yet. Please try again.',
        );
        return;
      }

      if (
        !(promoCodeSpecials ?? []).some(
          (special: SpecialGroup) => special.groupId === promoCodeSpecial.groupId,
        )
      ) {
        dispatch(
          updateSpecials({
            promoCodeSpecials: [...(promoCodeSpecials ?? []), promoCodeSpecial],
          }),
        );
      }

      const singleGift = getSinglePromoGiftToAutoApply(promoCodeSpecial);
      if (singleGift) {
        await handleUpdateSpecials([singleGift], promoCodeSpecial);
        return;
      }
      showSpecialsModal(promoCodeSpecial, []);
    } catch (error: unknown) {
      const technicalMessage = error instanceof Error ? error.message : String(error);
      handleError(`Form Viewer - Error validating promo code: ${technicalMessage}`);
      // Prefer API expired/invalid copy from rejected HTTP bodies; never surface transport text.
      showPromoCodeError(getValidateSpecialsApiMessage(error));
    } finally {
      setIsValidatingPromoCode(false);
    }
  };

  const handlePromoCodeChange = (event: any) => {
    setPromoCode(event.target.value);
    setPromoCodeErrorVisibility('hidden');
    setPromoCodeStatusColor('');
    setPromoCodeErrorMessage('');
  };

  async function handleUpdateSpecials(updatedSpecials: Special[], specialGroup: SpecialGroup) {
    setIsSavingSpecials(true);
    try {
      // Remove all specials in this group from the overall selected specials
      let newSelectedSpecials = selectedSpecials.filter(
        (special: Special) => special.groupId !== specialGroup.groupId,
      );
      // Add the newly selected group specials back to the overall selected specials
      newSelectedSpecials = newSelectedSpecials.concat(updatedSpecials);
      // Save backend first, then update client state so cart values stay in sync.
      await updateSpecialsInApplicantResponse(newSelectedSpecials);
      dispatch(updateSelectedSpecials(newSelectedSpecials));
      setPromoCode('');

      onSpecialsModalClose();
    } catch (error) {
      if (addOnPageIndex === -1) {
        handleError(`Form Viewer - Error saving specials: ${error}`, 'Error saving specials');
      }
    } finally {
      setIsSavingSpecials(false);
    }
  }

  async function updateSpecialsInApplicantResponse(specials: Special[]) {
    if (addOnPageIndex !== -1) {
      await saveOptionsPageSynchronously(addOnPageIndex, addOns ?? [], specials);
      return;
    }

    const targetPageIndex = pages.findIndex((page) => page.type === PageType.Blank);
    const targetPageId = pages[targetPageIndex]?.id;
    const targetPageResponses = getPageData(targetPageIndex).filter(
      (response) => !Object.prototype.hasOwnProperty.call(response, 'selectedSpecials'),
    );
    const targetPageErrorCount = leasingJourneyErrors[targetPageIndex];
    const applicantResponse = {
      currentPageId: targetPageId,
      targetPageId,
      applicationId,
      propertyId,
      responses: [...targetPageResponses, { selectedSpecials: specials }],
      errorCount: targetPageErrorCount ?? 0,
    };
    await patchSynchronously(applicantResponse);
    await refreshScheduledCharges();
  }

  const isStudentBulkUnitAssignment =
    occupancyType === OccupancyType.Student &&
    studentSelectionMode === StudentSelectionMode.BULK_UNIT_ASSIGNMENT;
  const hasUnitOrFloorPlanContext = isStudentBulkUnitAssignment
    ? Boolean(floorPlan?.id)
    : Boolean(unit);
  // Promo entry is Builder-controlled; still show the card when specials are already selected
  // so applicants can review/remove them even if promo entry is disabled.
  const canRenderSpecials =
    hasUnitOrFloorPlanContext && (isPromoEntryEnabled || visibleSelectedSpecials.length > 0);

  return (
    canRenderSpecials && (
      <>
        <Accordion defaultIndex={[0, 1, 2]} allowMultiple>
          <AccItems>
            <h2>
              <AccordionButton>
                <Box flex="1" textAlign="left">
                  {tSystem(SystemTranslationKeys.cart.specialPromotions)}
                </Box>
                <AccIcon />
              </AccordionButton>
            </h2>
            <AccordionPanel pb={4}>
              {isPromoEntryEnabled && (
                <>
                  <FormLabel id="specialsPromoCodeLabel" color={promoCodeStatusColor}>
                    {promoCodeLabel}
                  </FormLabel>
                  <Grid templateColumns="repeat(3, 1fr)" gap="2">
                    <GridItem colSpan={2}>
                      <Input
                        color={promoCodeStatusColor}
                        borderColor={promoCodeStatusColor}
                        value={promoCode}
                        id="specialsPromoCodeInput"
                        data-testid="specialsPromoCodeInput"
                        onChange={handlePromoCodeChange}
                        isDisabled={isApplicationLocked}
                      />
                    </GridItem>
                    <Button
                      variant="brand"
                      fontSize={11}
                      height="46px"
                      isDisabled={isApplicationLocked || isSavingSpecials || isValidatingPromoCode}
                      isLoading={isSavingSpecials || isValidatingPromoCode}
                      onClick={applyPromoCode}
                      data-testid="specialsPromoCodeApplyButton"
                    >
                      {tSystem(SystemTranslationKeys.cart.applyCode)}
                    </Button>
                  </Grid>
                  <Text
                    id="specialsPromoCodeErrorText"
                    data-testid="specialsPromoCodeErrorText"
                    visibility={promoCodeErrorVisibility as any}
                    fontSize="2xs"
                    fontStyle="italic"
                    color="red"
                  >
                    {promoCodeErrorMessage ||
                      t(
                        SystemTranslationKeys.cart.invalidPromoCode,
                        'The promo code you entered is invalid. Please try again.',
                      )}
                  </Text>
                </>
              )}
              {allAvailableSpecialsForUnit.map((specialGroup) => {
                const selectedSpecialsInGroup = visibleSelectedSpecials.filter(
                  (special) => special.groupId === specialGroup.groupId,
                );
                if (selectedSpecialsInGroup.length === 0) return null;
                const isEditable =
                  Number(specialGroup.specialLimit) !== Number(specialGroup.specialCount) ||
                  !!specialGroup.couponCode;
                const isSingleGiftPromo =
                  !!specialGroup.couponCode && getSinglePromoGiftToAutoApply(specialGroup) !== null;
                const showEditButton = isEditable && !isSingleGiftPromo;
                const showRemoveButton = isEditable;
                const specialGroupHeading = specialGroup.couponCode
                  ? `${specialGroup.couponCode} - ${specialGroup.groupName}`
                  : specialGroup.groupName;
                return (
                  <Box key={specialGroup.groupId} mb={5} mt={5}>
                    <Grid
                      gap={2}
                      p={2}
                      mt={3}
                      border="1px solid"
                      borderColor="silver.highlight"
                      borderRadius="16px"
                      boxShadow="0px 1px 3px rgba(0, 0, 0, 0.15)"
                    >
                      <Text whiteSpace="pre-line" fontWeight="bold" fontSize="xs">
                        {specialGroupHeading}
                      </Text>
                      {selectedSpecialsInGroup.map((special: any, index: number) => (
                        <Text
                          key={special.specialId ?? `${special.specialName}-${index}`}
                          whiteSpace="pre-line"
                          fontSize="xs"
                        >
                          {special.specialName}
                        </Text>
                      ))}
                      <Grid templateColumns="repeat(8, 1fr)" gap="2">
                        <GridItem colSpan={6}>
                          {specialGroup.termsAndConditions && (
                            <Text
                              fontSize={10}
                              display={{ base: 'inline', md: 'block' }}
                              color="blue.dusty"
                              cursor="pointer"
                              data-testid={`terms-and-condtions-card-${specialGroup.groupId}`}
                              onClick={(e) => {
                                e.preventDefault();
                                showTermsAndConditions(specialGroup.termsAndConditions);
                              }}
                            >
                              {t(
                                SystemTranslationKeys.footer.termsAndConditions,
                                'Terms & Conditions',
                              )}
                            </Text>
                          )}
                        </GridItem>
                        {(showEditButton || showRemoveButton) && (
                          <>
                            {showEditButton && (
                              <GridItem>
                                <Button
                                  size="xs"
                                  width={5}
                                  p={1}
                                  bg="silver.light"
                                  color="black.base"
                                  fontWeight="semibold"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    showSpecialsModal(specialGroup, selectedSpecialsInGroup);
                                  }}
                                >
                                  <PencilIcon />
                                </Button>
                              </GridItem>
                            )}
                            {showRemoveButton && (
                              <GridItem colStart={showEditButton ? undefined : 8}>
                                <Button
                                  size="xs"
                                  width={5}
                                  p={1}
                                  bg="silver.light"
                                  color="black.base"
                                  fontWeight="semibold"
                                  onClick={() =>
                                    openConfirmationModalForRemovingSpecials(specialGroup)
                                  }
                                >
                                  <CrossIcon />
                                </Button>
                              </GridItem>
                            )}
                          </>
                        )}
                      </Grid>
                    </Grid>
                  </Box>
                );
              })}
            </AccordionPanel>
          </AccItems>
        </Accordion>
        <OkModal
          isOpen={isShowTCOpen}
          onClose={onShowTCClose}
          modalHeader={tSystem(SystemTranslationKeys.footer.termsAndConditions)}
          modalText={termsAndConditions}
        />
        <SpecialsModal
          modalTitle={tSystem(SystemTranslationKeys.cart.specialPromotions)}
          specialGroup={specialGroupToEdit}
          isOpen={isSpecialsModalOpen}
          onClose={onSpecialsModalClose}
          selectedSpecials={selectedSpecialsInGroupToEdit ?? []}
          handleSave={handleUpdateSpecials}
          isSaving={isSavingSpecials}
        />
        <ConfirmationModal
          isOpen={isRemoveSpecialGroupOpen}
          onClose={onRemoveSpecialGroupClose}
          onClickAction={handleSpecialGroupRemove}
          modalText={specialGroupRemoveConfirmationMessage}
          actionBtnVariant="danger"
          actionBtnText={tSystem(SystemTranslationKeys.common.delete)}
          isLoading={isDeletingSpecial}
          loadingText={tSystem(SystemTranslationKeys.common.deleting)}
        />
      </>
    )
  );
}

/** Prefer server copy on rejected validate responses (`response.data.result.message`). */
function getValidateSpecialsApiMessage(error: unknown): string | undefined {
  if (!error || typeof error !== 'object') {
    return undefined;
  }
  const message = (error as { response?: { data?: { result?: { message?: unknown } } } }).response
    ?.data?.result?.message;
  return typeof message === 'string' && message.trim() ? message : undefined;
}

function normalizePromoCode(value: string | null | undefined): string {
  return value?.trim().toLowerCase() ?? '';
}

const AccItems = styled(AccordionItem)`
  border-top: 1px solid ${({ theme }: any) => theme.colors.gray.light};
`;
const AccIcon = styled(AccordionIcon)`
  background: ${({ theme }: any) => theme.colors.gray.light};
  border-radius: 4px;
  width: 25px;
  height: 25px;
`;
